import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { apiClient } from '@/api/client';
import { Search, CheckCircle, XCircle, RefreshCw, Edit, Plus, Wallet, Loader2, X } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose
} from '@/components/ui/dialog';

interface RestaurantSmsStatusListProps {
  organisationId: string;
  restaurants: any[];
}

interface SelectedRestaurant {
  restaurant_id: string;
  restaurant_name: string;
  sms_enabled?: boolean;
  stripe_customer_id?: string;
  isNewRecord?: boolean;
}

export const RestaurantSmsStatusList: React.FC<RestaurantSmsStatusListProps> = ({ 
  organisationId,
  restaurants
}) => {
  const [loading, setLoading] = useState(false);
  const [statusData, setStatusData] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [showStripeModal, setShowStripeModal] = useState(false);
  const [selectedRestaurant, setSelectedRestaurant] = useState<any>(null);
  const [newStripeCustomerId, setNewStripeCustomerId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Filter restaurants based on search query
  const filteredRestaurants = restaurants.filter(rest =>
    rest.restaurant_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    rest.restaurant_id?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Load SMS status data when component mounts or organisationId/restaurants change
  useEffect(() => {
    if (organisationId && restaurants.length > 0) {
      loadSmsStatusData();
    }
  }, [organisationId, restaurants]);

  // Load SMS status data from the database
  const loadSmsStatusData = async () => {
    setLoading(true);
    try {
      const restaurantIds = restaurants.map(r => r.restaurant_id);
      
      // Fetch restaurant SMS status
      const { data: smsStatus, error: smsError } = await supabase
        .from('restaurant_sms_status')
        .select('*')
        .in('restaurant_id', restaurantIds);
        
      if (smsError) throw smsError;
      
      // Fetch Stripe mappings
      const { data: stripeMappings, error: stripeError } = await supabase
        .from('restaurant_stripe_mapping')
        .select('*')
        .in('restaurant_id', restaurantIds);
        
      if (stripeError) throw stripeError;
      
      // Create lookup maps
      const smsStatusMap = Object.fromEntries(
        (smsStatus || []).map(status => [status.restaurant_id, status])
      );
      
      const stripeMappingMap = Object.fromEntries(
        (stripeMappings || []).map(mapping => [mapping.restaurant_id, mapping])
      );
      
      // Combine data with restaurant info
      const combinedData = filteredRestaurants.map(restaurant => {
        const smsData = smsStatusMap[restaurant.restaurant_id];
        const stripeData = stripeMappingMap[restaurant.restaurant_id];
        
        return {
          restaurant_id: restaurant.restaurant_id,
          restaurant_name: restaurant.restaurant_name,
          sms_enabled: smsData?.sms_enabled || false,
          subscription_item_id: smsData?.subscription_item_id,
          current_period_messages: smsData?.current_period_messages || 0,
          sms_enabled_date: smsData?.sms_enabled_date,
          disabled_date: smsData?.disabled_date,
          stripe_customer_id: stripeData?.stripe_customer_id,
          last_usage_update: smsData?.last_usage_update
        };
      });
      
      setStatusData(combinedData);
    } catch (error) {
      console.error('Error loading SMS status data:', error);
      toast({
        title: "Failed to load SMS status data",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Toggle SMS status for a restaurant
  const toggleSmsStatus = async (restaurantId: string, currentStatus: boolean) => {
    setUpdatingId(restaurantId);
    try {
      // Use apiClient with correct restaurant-sms endpoints
      const endpoint = `/api/restaurant-sms/${currentStatus ? 'disable' : 'enable'}/${restaurantId}`;
      const result = await apiClient.post(endpoint);
      
      if (result.data.success) {
        toast({
          title: `SMS ${currentStatus ? 'disabled' : 'enabled'}`,
          description: `SMS messaging has been ${currentStatus ? 'disabled' : 'enabled'} for the restaurant`,
        });
        
        // Update local state
        setStatusData(prevData => prevData.map(item => 
          item.restaurant_id === restaurantId
            ? { ...item, sms_enabled: !currentStatus }
            : item
        ));
      } else {
        throw new Error(result.data.error || 'Unknown error occurred');
      }
    } catch (error) {
      console.error('Error toggling SMS status:', error);
      toast({
        title: "Failed to update SMS status",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive"
      });
    } finally {
      setUpdatingId(null);
    }
  };

  // Open modal to manage Stripe customer ID
  const openStripeModal = (restaurant: any, isNewRecord = false) => {
    setSelectedRestaurant({
      ...restaurant,
      isNewRecord
    });
    setNewStripeCustomerId(restaurant.stripe_customer_id || '');
    setShowStripeModal(true);
  };

  const updateStripeCustomerId = async () => {
    if (!selectedRestaurant || !selectedRestaurant.restaurant_id) return;
    
    setSubmitting(true);
    try {
      // First update the Stripe customer ID mapping
      const updateResult = await apiClient.post('/api/admin/stripe/update-customer-id', {
        restaurant_id: selectedRestaurant.restaurant_id,
        stripe_customer_id: newStripeCustomerId
      });
      
      if (!updateResult.data.success) {
        throw new Error(updateResult.data.error || 'Failed to update Stripe customer ID');
      }
      
      // If this is a new record and user wants to enable SMS, do that too
      if (selectedRestaurant.isNewRecord && !selectedRestaurant.sms_enabled) {
        const enableResult = await apiClient.post(`/api/restaurant-sms/enable/${selectedRestaurant.restaurant_id}`, {
          stripe_customer_id: newStripeCustomerId // Pass the customer ID to the API
        });
        
        if (!enableResult.data.success) {
          throw new Error(enableResult.data.error || 'Failed to enable SMS');
        }
        
        toast({
          title: "SMS Enabled with Stripe Integration",
          description: "The restaurant is now connected to Stripe and SMS is enabled",
        });
      } else {
        toast({
          title: "Stripe Integration Updated",
          description: "The restaurant's Stripe customer ID has been updated",
        });
      }
      
      // Close the modal and reset the form
      setShowStripeModal(false);
      setNewStripeCustomerId('');
      setSelectedRestaurant(null);
      
      // Refresh data
      loadSmsStatusData();
    } catch (error) {
      console.error('Error updating Stripe customer:', error);
      toast({
        title: "Failed to update Stripe integration",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Restaurant SMS Status</CardTitle>
          <CardDescription>
            Manage SMS status for individual restaurants in this organisation
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Search */}
            <div className="flex items-center">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground/70" />
                <Input
                  placeholder="Search restaurants..."
                  className="pl-8"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              
              <Button 
                variant="outline" 
                className="ml-4"
                onClick={loadSmsStatusData}
                disabled={loading}
              >
                <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
            
            {/* Restaurant SMS status table */}
            {loading && statusData.length === 0 ? (
              <div className="py-4 text-center">Loading SMS status data...</div>
            ) : statusData.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Restaurant</TableHead>
                    <TableHead>SMS Status</TableHead>
                    <TableHead className="hidden md:table-cell">Period Usage</TableHead>
                    <TableHead className="hidden md:table-cell">Last Updated</TableHead>
                    <TableHead className="hidden lg:table-cell">Payment Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRestaurants.length > 0 ? (
                    statusData
                      .filter(item => 
                        filteredRestaurants.some(r => r.restaurant_id === item.restaurant_id)
                      )
                      .map((item) => (
                        <TableRow key={item.restaurant_id}>
                          <TableCell>
                            <div className="font-medium">{item.restaurant_name}</div>
                            <div className="text-xs text-muted-foreground truncate">
                              {item.restaurant_id}
                            </div>
                          </TableCell>
                          <TableCell>
                            {item.sms_enabled ? (
                              <Badge className="bg-green-500 hover:bg-green-600">
                                <CheckCircle className="mr-1 h-3 w-3" />
                                Enabled
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="border-muted-foreground/30">
                                <XCircle className="mr-1 h-3 w-3" />
                                Disabled
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="hidden md:table-cell">
                            {item.current_period_messages || 0} segments
                          </TableCell>
                          <TableCell className="hidden md:table-cell">
                            {item.last_usage_update 
                              ? new Date(item.last_usage_update).toLocaleString()
                              : 'Never'
                            }
                          </TableCell>
                          <TableCell className="hidden lg:table-cell">
                            {item.stripe_customer_id ? (
                              <Badge className="bg-blue-500 hover:bg-blue-600">
                                <Wallet className="mr-1 h-3 w-3" />
                                Active
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="border-muted-foreground/30">
                                <XCircle className="mr-1 h-3 w-3" />
                                No Billing
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center space-x-2">
                              <Switch
                                checked={item.sms_enabled}
                                disabled={updatingId === item.restaurant_id}
                                onCheckedChange={() => toggleSmsStatus(item.restaurant_id, item.sms_enabled)}
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openStripeModal(item, !item.stripe_customer_id)}
                                className="h-8 px-2"
                                title={item.stripe_customer_id ? "Edit Stripe Customer ID" : "Add Stripe Customer ID"}
                              >
                                {item.stripe_customer_id ? (
                                  <Edit className="h-4 w-4" />
                                ) : (
                                  <Plus className="h-4 w-4" />
                                )}
                              </Button>
                              <span className="text-xs">
                                {updatingId === item.restaurant_id ? 'Updating...' : ''}
                              </span>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-4">
                        No restaurants match your search criteria
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            ) : (
              <div className="py-8 text-center border rounded-lg">
                <XCircle className="mx-auto h-12 w-12 text-muted-foreground" />
                <h3 className="mt-4 text-lg font-medium">No Restaurants Available</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  This organisation doesn't have any restaurants yet.
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Stripe Customer Modal */}
      <Dialog open={showStripeModal} onOpenChange={setShowStripeModal}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>
              {selectedRestaurant?.stripe_customer_id 
                ? 'Edit Stripe Customer ID' 
                : 'Set Up Restaurant Billing'}
            </DialogTitle>
            <DialogDescription>
              {selectedRestaurant?.stripe_customer_id 
                ? `Update the Stripe Customer ID for ${selectedRestaurant?.restaurant_name || 'this restaurant'}.`
                : `Enter a Stripe Customer ID to set up direct billing for ${selectedRestaurant?.restaurant_name || 'this restaurant'}.`}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="stripeCustomerId" className="col-span-4">
                Stripe Customer ID
              </Label>
              <Input
                id="stripeCustomerId"
                placeholder="cus_..."
                className="col-span-4"
                value={newStripeCustomerId}
                onChange={(e) => setNewStripeCustomerId(e.target.value)}
              />
              <div className="col-span-4 text-xs text-muted-foreground">
                {selectedRestaurant?.stripe_customer_id 
                  ? 'This will override the organisation-level billing for this restaurant.'
                  : 'Setting up restaurant-level billing will take precedence over organisation billing.'}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowStripeModal(false);
                setNewStripeCustomerId('');
                setSelectedRestaurant(null);
              }}
            >
              <X className="mr-2 h-4 w-4" />
              Cancel
            </Button>
            <Button 
              onClick={updateStripeCustomerId} 
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  {selectedRestaurant?.stripe_customer_id ? (
                    <>
                      <Edit className="mr-2 h-4 w-4" />
                      Update
                    </>
                  ) : (
                    <>
                      <Plus className="mr-2 h-4 w-4" />
                      Add
                    </>
                  )}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};